import { useMemo } from "react";
import { Zap, Sun, Flame, BatteryCharging, Undo2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EnergyAreaPanel,
  EnergyGauge,
  EnergyFlowChart,
  GlanceCard,
  MeterRow,
  type InsightPoint,
} from "@/components/system";
import { trpc } from "@/lib/trpc";
import {
  buildEnergyFlowSeries,
  buildEnergyView,
  dailyDeltaFromCumulative,
  dailyDirectUsePct,
  findSolarDeviceId,
  lastInsightValue,
  mapInsightEntries,
  sumInsightSeriesByTimestamp,
} from "@/lib/homey-subviews";
import type { EnergyStatVM, HomeySnapshot } from "@/types/homey";

// ---------------------------------------------------------------------------
// Stat icons + domains, keyed by EnergyStatVM.key
// ---------------------------------------------------------------------------

const STAT_ICON = {
  today: <Zap />,
  solar: <Sun />,
  gas: <Flame />,
  battery: <BatteryCharging />,
  returned: <Undo2 />,
} as const;

type StatKey = keyof typeof STAT_ICON;

const STAT_DOMAIN: Record<StatKey, "energy" | "light" | "climate"> = {
  today: "energy",
  solar: "light",
  gas: "climate",
  battery: "energy",
  returned: "energy",
};

function statIcon(key: string) {
  return STAT_ICON[key as StatKey] ?? <Zap />;
}

function statDomain(key: string) {
  return STAT_DOMAIN[key as StatKey] ?? "energy";
}

// ---------------------------------------------------------------------------
// Timestamp -> HH:MM label. mapInsightEntries keeps `t` raw (pure); labeling
// is a page concern. Handles ISO strings and epoch-ms numeric strings.
// ---------------------------------------------------------------------------

function toClockLabel(rawT: string): string {
  const asNumber = Number(rawT);
  const date =
    Number.isFinite(asNumber) && rawT.trim() !== "" ? new Date(asNumber) : new Date(rawT);
  if (Number.isNaN(date.getTime())) return rawT;
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}

function labelInsightPoints(points: InsightPoint[]): InsightPoint[] {
  return points.map((p) => ({ t: toClockLabel(p.t), value: p.value }));
}

// ---------------------------------------------------------------------------
// EnergyView
// ---------------------------------------------------------------------------

interface EnergyViewProps {
  snapshot: HomeySnapshot;
}

/**
 * Energy sub-view: live whole-home draw gauge, headline stat cards, a top
 * consumers bar list, and a 24h power InsightChart from the grid meter.
 *
 * To test: open the dashboard, switch to the Energy tab. The gauge should show
 * the grid meter's live watts, stat cards reflect today's kWh / solar / gas /
 * battery / returned, the top consumers list ranks devices by draw, the
 * "Energy today" consumption chart populates once getInsightEntries resolves,
 * and (when a solarpanel device is present) a "Solar production (today)" chart
 * and an "Energy flow (today)" chart appear below it. The flow chart overlays
 * production, whole-home consumption (production + summed l1/l2/l3 grid net),
 * and the directly-used solar slice, with a "Direct use %" headline.
 */
export function EnergyView({ snapshot }: EnergyViewProps) {
  const view = useMemo(() => buildEnergyView(snapshot), [snapshot]);

  const { powerNowW, gridMeterDeviceId, topConsumers } = view;

  // 24h cumulative energy for the grid meter. We chart meter_power.daily (kWh
  // today) rather than measure_power: P1 meters report live wattage but do not
  // log instantaneous power to Insights (that series is all-null), whereas the
  // daily kWh meter is logged at full resolution.
  const logId =
    gridMeterDeviceId !== null ? `homey:device:${gridMeterDeviceId}:meter_power.daily` : "";

  const insightQuery = trpc.homey.getInsightEntries.useQuery(
    { logId, resolution: "today" },
    { enabled: gridMeterDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );

  const chartData = useMemo(
    () => labelInsightPoints(mapInsightEntries(insightQuery.data ?? null)),
    [insightQuery.data],
  );

  // Returned energy TODAY. meter_power.returned is a LIFETIME totalizer (never
  // resets), so reading the capability directly shows ~13000+ kWh. Instead we
  // pull today's insight series for that register and take last - first (the
  // energy returned since midnight) via dailyDeltaFromCumulative.
  const returnedLogId =
    gridMeterDeviceId !== null ? `homey:device:${gridMeterDeviceId}:meter_power.returned` : "";

  const returnedQuery = trpc.homey.getInsightEntries.useQuery(
    { logId: returnedLogId, resolution: "today" },
    { enabled: gridMeterDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );

  const returnedToday = useMemo(
    () => dailyDeltaFromCumulative(mapInsightEntries(returnedQuery.data ?? null)),
    [returnedQuery.data],
  );

  // Solar production over the day. The solarpanel device logs meter_power.day
  // ("Opgewekt Vandaag" — kWh produced today), a daily-resetting cumulative
  // series that climbs from 0 at midnight to today's total. This mirrors the
  // grid meter's meter_power.daily consumption series exactly. The solar device
  // does NOT log measure_power to Insights, so meter_power.day is the only
  // chartable production series.
  const solarDeviceId = useMemo(() => findSolarDeviceId(snapshot), [snapshot]);

  const solarLogId = solarDeviceId !== null ? `homey:device:${solarDeviceId}:meter_power.day` : "";

  const solarQuery = trpc.homey.getInsightEntries.useQuery(
    { logId: solarLogId, resolution: "today" },
    { enabled: solarDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );

  const solarChartData = useMemo(
    () => labelInsightPoints(mapInsightEntries(solarQuery.data ?? null)),
    [solarQuery.data],
  );

  // ---- Energy flow (today): production vs consumption vs direct use --------
  // Grid net power is the sum of the three phase logs. The aggregate
  // measure_power log on this P1 meter is all-null, so we MUST sum l1+l2+l3.
  // Sign: >0 = importing, <0 = exporting (midday the sum goes negative).
  const gridL1Query = trpc.homey.getInsightEntries.useQuery(
    {
      logId: gridMeterDeviceId !== null ? `homey:device:${gridMeterDeviceId}:measure_power.l1` : "",
      resolution: "today",
    },
    { enabled: gridMeterDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );
  const gridL2Query = trpc.homey.getInsightEntries.useQuery(
    {
      logId: gridMeterDeviceId !== null ? `homey:device:${gridMeterDeviceId}:measure_power.l2` : "",
      resolution: "today",
    },
    { enabled: gridMeterDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );
  const gridL3Query = trpc.homey.getInsightEntries.useQuery(
    {
      logId: gridMeterDeviceId !== null ? `homey:device:${gridMeterDeviceId}:measure_power.l3` : "",
      resolution: "today",
    },
    { enabled: gridMeterDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );

  // Live production power (W). The solar device logs energy_power (instantaneous
  // watts) to Insights — distinct from meter_power.day (kWh cumulative) used by
  // the production chart above.
  const productionPowerQuery = trpc.homey.getInsightEntries.useQuery(
    {
      logId: solarDeviceId !== null ? `homey:device:${solarDeviceId}:energy_power` : "",
      resolution: "today",
    },
    { enabled: solarDeviceId !== null, retry: false, staleTime: 5 * 60 * 1000 },
  );

  const flowData = useMemo(() => {
    const production = mapInsightEntries(productionPowerQuery.data ?? null);
    const l1 = mapInsightEntries(gridL1Query.data ?? null);
    const l2 = mapInsightEntries(gridL2Query.data ?? null);
    const l3 = mapInsightEntries(gridL3Query.data ?? null);
    const gridNet = sumInsightSeriesByTimestamp([l1, l2, l3]);
    const series = buildEnergyFlowSeries(production, gridNet);
    // Relabel timestamps to clock labels (page concern) without touching values.
    return series.map((p) => ({ ...p, t: toClockLabel(p.t) }));
  }, [productionPowerQuery.data, gridL1Query.data, gridL2Query.data, gridL3Query.data]);

  const flowLoading =
    productionPowerQuery.isLoading ||
    gridL1Query.isLoading ||
    gridL2Query.isLoading ||
    gridL3Query.isLoading;

  // Daily totals for the energy-flow chart header (kWh), plus direct-use %:
  //   productionKwh — solar produced today: meter_power.day RESETS daily, so
  //     it's the last sample (lastInsightValue), not a delta.
  //   exportKwh     — energy fed back today: daily delta of meter_power.returned.
  //   directUseKwh  — produced minus exported, guarded against nulls/negatives.
  //   gridImportKwh — consumption from the grid today: the "Today" stat
  //     (meter_power.daily) the snapshot builder already surfaced.
  //   directUsePct  — share of production consumed directly.
  const flowHeader = useMemo(() => {
    const productionKwh = lastInsightValue(mapInsightEntries(solarQuery.data ?? null));
    const exportKwh = dailyDeltaFromCumulative(mapInsightEntries(returnedQuery.data ?? null));
    const directUseKwh =
      productionKwh !== null && exportKwh !== null ? Math.max(0, productionKwh - exportKwh) : null;
    const gridImportKwh = view.stats.find((s) => s.key === "today")?.value ?? null;
    const directUsePct = dailyDirectUsePct(productionKwh, exportKwh);
    return { productionKwh, directUseKwh, gridImportKwh, directUsePct };
  }, [solarQuery.data, returnedQuery.data, view.stats]);

  // Inject the derived "Returned" stat after the snapshot-built stats, mirroring
  // the documented order (today, solar, gas, battery, returned). Only when we
  // have a grid meter and a non-null daily delta.
  const stats: EnergyStatVM[] = useMemo(() => {
    if (gridMeterDeviceId === null || returnedToday === null) return view.stats;
    return [
      ...view.stats,
      { key: "returned", label: "Returned", value: returnedToday, unit: "kWh", precision: 1 },
    ];
  }, [view.stats, gridMeterDeviceId, returnedToday]);

  // Empty state: nothing energy-related to show at all.
  const hasEnergyData = powerNowW !== null || stats.length > 0 || topConsumers.length > 0;
  if (!hasEnergyData) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <Zap className="size-8 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">No energy data reported by your Homey.</p>
      </div>
    );
  }

  // Gauge ceiling: at least 3500 W, expand if live draw exceeds it.
  const gaugeMax = Math.max(3500, powerNowW ?? 0);
  const topWatts = topConsumers[0]?.watts ?? 1;

  return (
    <div className="space-y-8">
      {/* Header: gauge + stat cards */}
      <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:gap-12">
        {powerNowW !== null && (
          <EnergyGauge value={powerNowW} max={gaugeMax} label="Power now" unit="W" />
        )}
        {stats.length > 0 && (
          <div className="grid w-full grid-cols-2 gap-3 lg:grid-cols-3">
            {stats.map((stat) => (
              <GlanceCard
                key={stat.key}
                label={stat.label}
                value={stat.value}
                unit={stat.unit}
                precision={stat.precision}
                icon={statIcon(stat.key)}
                domain={statDomain(stat.key)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 24h power chart — the shared dark EnergyAreaPanel reads as a sibling of
          the EnergyFlowChart hero panel below. */}
      {gridMeterDeviceId !== null && (
        <EnergyAreaPanel
          icon={<Zap aria-hidden />}
          title="Energy today"
          legendLabel="Consumption"
          domain="energy"
          unit=" kWh"
          data={chartData}
          loading={insightQuery.isLoading}
          emptyText="No power history available yet."
        />
      )}

      {/* Solar production chart. Renders only when a solar device exists AND it
          has a non-empty series — while loading we show a skeleton, but we never
          render an empty chart box (degrade gracefully to nothing). */}
      {solarDeviceId !== null && (solarQuery.isLoading || solarChartData.length > 0) && (
        <EnergyAreaPanel
          icon={<Sun aria-hidden />}
          title="Solar production (today)"
          legendLabel="Production"
          domain="light"
          unit=" kWh"
          data={solarChartData}
          loading={solarQuery.isLoading}
        />
      )}

      {/* Energy flow (today): the HomeWizard-style "Self-sufficiency" hero
          panel. The chart owns its own dark panel, header (icon + coloured kWh
          totals) and legend, so no separate section heading is needed here.
          Renders only when a solar device exists AND we have a non-empty merged
          series — degrade gracefully (skeleton while loading, nothing empty). */}
      {solarDeviceId !== null && (flowLoading || flowData.length > 0) && (
        <section>
          {flowLoading ? (
            <Skeleton className="h-[22rem] w-full rounded-3xl" />
          ) : (
            <EnergyFlowChart
              data={flowData}
              directUseKwh={flowHeader.directUseKwh}
              gridImportKwh={flowHeader.gridImportKwh}
              productionKwh={flowHeader.productionKwh}
              directUsePct={flowHeader.directUsePct}
              unit=" W"
            />
          )}
        </section>
      )}

      {/* Top consumers */}
      {topConsumers.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-base font-semibold">Top consumers</h2>
          <div className="space-y-4">
            {topConsumers.map((c) => (
              <MeterRow
                key={c.id}
                label={c.name}
                value={c.watts}
                unit="W"
                max={topWatts}
                domain="energy"
                icon={<Zap />}
                precision={0}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default EnergyView;
